import React, { useState } from 'react';
import styled from 'styled-components';
import { theme } from '../../../theme/GlobalTheme';
import { IFE, MFE, MIF } from '../../../utils/viewUtils';
import { getBlanketDisplayText } from '../../../utils/blanketDisplayText';

const SectionContainer = styled.div`
  margin-bottom: ${theme.spacing.sm};
`;

const SectionHeader = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background-color: ${theme.colors.white};
  border: 1px solid ${theme.colors.primary};
  border-radius: ${theme.borderRadius.medium};
  cursor: pointer;
  font-size: ${theme.typography.fontSize.body};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.primary};
  font-family: ${theme.typography.fontFamily};
  text-align: left;
  transition: all 0.2s ease-in-out;

  &:hover {
    background-color: ${theme.colors.tertiary};
  }

  &:active {
    transform: translateY(0);
  }
`;

const SectionIcon = styled.span`
  transition: transform 0.2s ease-in-out;
  transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  font-size: ${theme.typography.fontSize.body};
`;

const SectionContent = styled.div`
  display: ${props => props.isOpen ? 'block' : 'none'};
  padding: 0 ${theme.spacing.md};
  background-color: #f8f9fa;
  border: 1px solid ${theme.colors.primary};
  border-top: none;
  border-radius: 0 0 ${theme.borderRadius.medium} ${theme.borderRadius.medium};
`;

const InputRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: ${theme.spacing.sm} 0;
  border-bottom: 1px solid ${theme.colors.border};

  &:last-child {
    border-bottom: none;
  }
`;

const InputLabel = styled.span`
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.textPrimary};
  flex: 2;
  margin-right: ${theme.spacing.md};
  min-width: 0;
  word-break: break-word;
`;

const InputValue = styled.span`
  color: ${theme.colors.text};
  flex: 1;
  text-align: right;
  min-width: 0;
  word-break: break-word;
`;

const ConfigurationInputs = ({ input, fusionMachineType }) => {
  const [openSections, setOpenSections] = useState({
    basic: false,
    power: false,
    blanket: false,
    materials: false,
    safety: false,
  });

  const toggleSection = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const formatValue = (value) => {
    if (value === null || value === undefined || value === '') {
      return 'Not set';
    }
    if (typeof value === 'number') {
      // Format numbers with appropriate decimal places
      if (Number.isInteger(value)) {
        return value.toString();
      }
      return value.toFixed(2).replace(/\.?0+$/, '');
    }
    if (typeof value === 'string') {
      // Format enum strings to be more readable
      return value.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
    return String(value);
  };

  const formatBlanketValue = (value) => {
    return getBlanketDisplayText(value);
  };

  return (
    <>
      {/* Basic Configuration Section */}
      <SectionContainer>
        <SectionHeader onClick={() => toggleSection('basic')}>
          <span>Basic Configuration</span>
          <SectionIcon isOpen={openSections.basic}>▼</SectionIcon>
        </SectionHeader>
        <SectionContent isOpen={openSections.basic}>
          {input?.basic && (
            <>
              <InputRow>
                <InputLabel>Fusion Power:</InputLabel>
                <InputValue>{formatValue(input.basic.fusionPower)} MW</InputValue>
              </InputRow>
              <InputRow>
                <InputLabel>Number of Units:</InputLabel>
                <InputValue>{formatValue(input.basic.numberModules)}</InputValue>
              </InputRow>
              <InputRow>
                <InputLabel>AM (Availability/Maintenance Factor):</InputLabel>
                <InputValue>{formatValue(input.basic.aM)}</InputValue>
              </InputRow>
              <InputRow>
                <InputLabel>Construction Time:</InputLabel>
                <InputValue>{formatValue(input.basic.constructionTime)} Years</InputValue>
              </InputRow>
              <InputRow>
                <InputLabel>Plant Lifetime:</InputLabel>
                <InputValue>{formatValue(input.basic.plantLifetime)} Years</InputValue>
              </InputRow>
              <InputRow>
                <InputLabel>Plant Availability Factor:</InputLabel>
                <InputValue>{formatValue(input.basic.plantAvailabilityFactor)}</InputValue>
              </InputRow>
            </>
          )}
        </SectionContent>
      </SectionContainer>

      {/* Safety & Hazards Configuration Section */}
      {input?.basic?.includeSafetyHazardsCosts && input?.safety?.tritiumRelease && (
        <SectionContainer>
          <SectionHeader onClick={() => toggleSection('safety')}>
            <span>Safety &amp; Hazards Configuration</span>
            <SectionIcon isOpen={openSections.safety}>▼</SectionIcon>
          </SectionHeader>
          <SectionContent isOpen={openSections.safety}>
            <InputRow>
              <InputLabel>Region:</InputLabel>
              <InputValue>{formatValue(input.basic.region)}</InputValue>
            </InputRow>
            <InputRow>
              <InputLabel>Tritium inventory (HTO):</InputLabel>
              <InputValue>{formatValue(input.safety.tritiumRelease.htoInventory_g)} g</InputValue>
            </InputRow>
            <InputRow>
              <InputLabel>Dust tritium inventory:</InputLabel>
              <InputValue>{formatValue(input.safety.tritiumRelease.dustInventory_g_perMonth)} g/month</InputValue>
            </InputRow>
            <InputRow>
              <InputLabel>Stack height:</InputLabel>
              <InputValue>{formatValue(input.safety.tritiumRelease.stackHeight_m)} m</InputValue>
            </InputRow>
          </SectionContent>
        </SectionContainer>
      )}

      {/* Power Configuration Section */}
      <SectionContainer>
        <SectionHeader onClick={() => toggleSection('power')}>
          <span>Power Configuration</span>
          <SectionIcon isOpen={openSections.power}>▼</SectionIcon>
        </SectionHeader>
        <SectionContent isOpen={openSections.power}>
          {input?.power && (
            <>
              {fusionMachineType === IFE && (
                <>
                  <InputRow>
                    <InputLabel>Power into Implosion Laser:</InputLabel>
                    <InputValue>{formatValue(input.power.powerIntoImplosionLaser)} MW</InputValue>
                  </InputRow>
                  <InputRow>
                    <InputLabel>Input Power Wall Plug Efficiency (Implosion):</InputLabel>
                    <InputValue>{formatValue(input.power.inputPowerWallPlugEfficiencyImplosion)}</InputValue>
                  </InputRow>
                  <InputRow>
                    <InputLabel>Power into Ignition Laser:</InputLabel>
                    <InputValue>{formatValue(input.power.powerIntoIgnitionLaser)} MW</InputValue>
                  </InputRow>
                  <InputRow>
                    <InputLabel>Input Power Wall Plug Efficiency (Ignition):</InputLabel>
                    <InputValue>{formatValue(input.power.inputPowerWallPlugEfficiencyIgnition)}</InputValue>
                  </InputRow>
                </>
              )}

              <InputRow>
                <InputLabel>Subsystem and Control Fraction:</InputLabel>
                <InputValue>{formatValue(input.power.subsystemAndControlFraction)}</InputValue>
              </InputRow>
              <InputRow>
                <InputLabel>Cryo Vacuum Pumping:</InputLabel>
                <InputValue>{formatValue(input.power.cryoVacuumPumping)} MW</InputValue>
              </InputRow>
              <InputRow>
                <InputLabel>Neutron Energy Multiplier:</InputLabel>
                <InputValue>{formatValue(input.power.neutronEnergyMultiplier)}</InputValue>
              </InputRow>
              <InputRow>
                <InputLabel>Pumping Power Capture Efficiency:</InputLabel>
                <InputValue>{formatValue(input.power.pumpingPowerCaptureEfficiency)}</InputValue>
              </InputRow>
              <InputRow>
                <InputLabel>Thermal Conversion Efficiency:</InputLabel>
                <InputValue>{formatValue(input.power.thermalConversionEfficiency)}</InputValue>
              </InputRow>
              <InputRow>
                <InputLabel>Primary Coolant Pumping Power Fraction:</InputLabel>
                <InputValue>{formatValue(input.power.primaryCoolantPumpingPowerFraction)}</InputValue>
              </InputRow>
              <InputRow>
                <InputLabel>Tritium Systems:</InputLabel>
                <InputValue>{formatValue(input.power.tritiumSystems)} MW</InputValue>
              </InputRow>
              <InputRow>
                <InputLabel>Housekeeping Power:</InputLabel>
                <InputValue>{formatValue(input.power.housekeepingPower)} MW</InputValue>
              </InputRow>

              {(fusionMachineType === MIF || fusionMachineType === MFE) && (
                <>
                  <InputRow>
                    <InputLabel>Power into TF Coils:</InputLabel>
                    <InputValue>{formatValue(input.power.powerIntoTfCoils)} MW</InputValue>
                  </InputRow>
                  <InputRow>
                    <InputLabel>TF Coil Cooling:</InputLabel>
                    <InputValue>{formatValue(input.power.tfCoilCooling)} MW</InputValue>
                  </InputRow>
                  <InputRow>
                    <InputLabel>Power into PF Coils:</InputLabel>
                    <InputValue>{formatValue(input.power.powerIntoPfCoils)} MW</InputValue>
                  </InputRow>
                  <InputRow>
                    <InputLabel>PF Coil Cooling:</InputLabel>
                    <InputValue>{formatValue(input.power.pfCoilCooling)} MW</InputValue>
                  </InputRow>
                  <InputRow>
                    <InputLabel>Input Power Wall Plug Efficiency:</InputLabel>
                    <InputValue>{formatValue(input.power.inputPowerWallPlugEfficiency)}</InputValue>
                  </InputRow>
                  <InputRow>
                    <InputLabel>Direct Energy Conversion Efficiency:</InputLabel>
                    <InputValue>{formatValue(input.power.directEnergyConversionEfficiency)}</InputValue>
                  </InputRow>
                  <InputRow>
                    <InputLabel>Heating Power:</InputLabel>
                    <InputValue>{formatValue(input.power.totalPowerInput)} MW</InputValue>
                  </InputRow>
                </>
              )}

              {fusionMachineType === MIF && (
                <>
                  <InputRow>
                    <InputLabel>Recovery Efficiency:</InputLabel>
                    <InputValue>{formatValue(input.power.recoveryEfficiency)}</InputValue>
                  </InputRow>
                  <InputRow>
                    <InputLabel>Compression System Efficiency:</InputLabel>
                    <InputValue>{formatValue(input.power.compressionSystemEfficiency)}</InputValue>
                  </InputRow>
                  <InputRow>
                    <InputLabel>Power Into Compression System:</InputLabel>
                    <InputValue>{formatValue(input.power.powerIntoCompressionSystem)} MW</InputValue>
                  </InputRow>
                  <InputRow>
                    <InputLabel>Recovered Power:</InputLabel>
                    <InputValue>{formatValue(input.power.recoveredPower)} MW</InputValue>
                  </InputRow>
                  <InputRow>
                    <InputLabel>Power for Plasma Target Formation:</InputLabel>
                    <InputValue>{formatValue(input.power.powerForPlasmaTargetFormation)} MW</InputValue>
                  </InputRow>
                  <InputRow>
                    <InputLabel>Target Formation Efficiency:</InputLabel>
                    <InputValue>{formatValue(input.power.targetFormationEfficiency)}</InputValue>
                  </InputRow>
                </>
              )}
            </>
          )}
        </SectionContent>
      </SectionContainer>

      {/* Blanket Configuration Section */}
      <SectionContainer>
        <SectionHeader onClick={() => toggleSection('blanket')}>
          <span>Blanket Configuration</span>
          <SectionIcon isOpen={openSections.blanket}>▼</SectionIcon>
        </SectionHeader>
        <SectionContent isOpen={openSections.blanket}>
          {input?.blanket && (
            <>
              <InputRow>
                <InputLabel>First Wall Type:</InputLabel>
                <InputValue>{formatBlanketValue(input.blanket.firstWallType)}</InputValue>
              </InputRow>
              <InputRow>
                <InputLabel>Blanket Type:</InputLabel>
                <InputValue>{formatBlanketValue(input.blanket.blanketType)}</InputValue>
              </InputRow>
              <InputRow>
                <InputLabel>Primary Coolant Type:</InputLabel>
                <InputValue>{formatBlanketValue(input.blanket.primaryCoolantType)}</InputValue>
              </InputRow>
              <InputRow>
                <InputLabel>Secondary Coolant Type:</InputLabel>
                <InputValue>{formatBlanketValue(input.blanket.secondaryCoolantType)}</InputValue>
              </InputRow>
              <InputRow>
                <InputLabel>Neutron Multiplier:</InputLabel>
                <InputValue>{formatBlanketValue(input.blanket.neutronMultiplier)}</InputValue>
              </InputRow>
              <InputRow>
                <InputLabel>Structural Material:</InputLabel>
                <InputValue>{formatBlanketValue(input.blanket.structure)}</InputValue>
              </InputRow>
            </>
          )}
        </SectionContent>
      </SectionContainer>

      {/* Materials Configuration Section */}
      <SectionContainer>
        <SectionHeader onClick={() => toggleSection('materials')}>
          <span>Materials Configuration</span>
          <SectionIcon isOpen={openSections.materials}>▼</SectionIcon>
        </SectionHeader>
        <SectionContent isOpen={openSections.materials}>
          {input?.materials && (
            <>
              <InputRow>
                <InputLabel>Cost of YBCO Tape:</InputLabel>
                <InputValue>${formatValue(input.materials.ybcoTapeCost)} /kAm</InputValue>
              </InputRow>
              <InputRow>
                <InputLabel>Cost of Stainless Steel:</InputLabel>
                <InputValue>${formatValue(input.materials.stainlessSteelCost)} /kg</InputValue>
              </InputRow>
              <InputRow>
                <InputLabel>Cost of Copper:</InputLabel>
                <InputValue>${formatValue(input.materials.copperCost)} /kg</InputValue>
              </InputRow>
              <InputRow>
                <InputLabel>MFR Factor:</InputLabel>
                <InputValue>{formatValue(input.materials.mfrFactor)}</InputValue>
              </InputRow>
            </>
          )}
        </SectionContent>
      </SectionContainer>
    </>
  );
};

export default ConfigurationInputs;
